import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import MultiImageUploader from "@/components/MultiImageUploader";
import { BookOpen, User, ImageIcon, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "lore", label: "Lore Entry", icon: BookOpen, desc: "Share universe lore, history, or articles" },
  { value: "character", label: "Character", icon: User, desc: "Suggest a new character bio" },
  { value: "fan_art", label: "Fan Art", icon: ImageIcon, desc: "Upload your fan art and creations" },
];

const SubmitContributionPage = () => {
  const navigate = useNavigate();
  const [universes, setUniverses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: "",
    title: "",
    content: "",
    universe_id: "",
    images: [],
    species: "",
    affiliation: "",
    abilities: "",
  });

  useEffect(() => {
    axios.get(`${API}/universes`).then(r => setUniverses(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.title.trim() || !form.content.trim()) {
      toast.error("Please fill in type, title, and content");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        content: form.content.trim(),
        universe_id: form.universe_id || null,
        images: form.images,
      };
      if (form.type === "character") {
        payload.species = form.species || null;
        payload.affiliation = form.affiliation || null;
        payload.abilities = form.abilities ? form.abilities.split(",").map(s => s.trim()).filter(Boolean) : [];
      }
      const res = await axios.post(`${API}/contributions`, payload);
      if (res.data.status === "approved") {
        toast.success("Contribution published! (Trusted contributor)");
      } else {
        toast.success("Submitted for review! An admin will approve it shortly.");
      }
      navigate("/community");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Submit Contribution" description="Contribute lore, characters, or fan art to FanVerse." />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-2xl mx-auto w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2">Submit a Contribution</h1>
          <p className="text-slate-400 text-sm">Share your lore, characters, or art with the community</p>
        </div>

        {/* Type Selection */}
        {!form.type ? (
          <div className="grid sm:grid-cols-3 gap-4" data-testid="type-selection">
            {TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => setForm({ ...form, type: t.value })}
                className="card-surface p-6 text-center hover:border-cyan-500/30 transition-all group"
                data-testid={`select-type-${t.value}`}
              >
                <t.icon className="w-8 h-8 text-cyan-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-semibold font-['Outfit'] mb-1">{t.label}</h3>
                <p className="text-slate-500 text-xs">{t.desc}</p>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contribution-form">
            {/* Type badge */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono uppercase tracking-wider text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded">
                {TYPES.find(t => t.value === form.type)?.label}
              </span>
              <button type="button" onClick={() => setForm({ ...form, type: "" })} className="text-slate-500 text-xs hover:text-white">Change</button>
            </div>

            {/* Universe */}
            <div>
              <Label className="text-slate-300">Universe (optional)</Label>
              <Select value={form.universe_id} onValueChange={(v) => setForm({ ...form, universe_id: v === "none" ? "" : v })}>
                <SelectTrigger className="input-dark mt-1" data-testid="contribution-universe">
                  <SelectValue placeholder="Select a universe" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10">
                  <SelectItem value="none">No specific universe</SelectItem>
                  {universes.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label className="text-slate-300">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={form.type === "character" ? "Character name" : form.type === "fan_art" ? "Artwork title" : "Lore entry title"}
                className="input-dark mt-1"
                required
                data-testid="contribution-title"
              />
            </div>

            {/* Content */}
            <div>
              <Label className="text-slate-300">{form.type === "fan_art" ? "Description" : "Content"}</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={form.type === "character" ? "Character biography and backstory..." : form.type === "fan_art" ? "Describe your artwork..." : "Write your lore entry..."}
                className="input-dark mt-1 min-h-[150px]"
                required
                data-testid="contribution-content"
              />
            </div>

            {/* Character-specific fields */}
            {form.type === "character" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Species</Label>
                  <Input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} className="input-dark mt-1" placeholder="e.g., Human, Kryptonian" data-testid="contribution-species" />
                </div>
                <div>
                  <Label className="text-slate-300">Affiliation</Label>
                  <Input value={form.affiliation} onChange={(e) => setForm({ ...form, affiliation: e.target.value })} className="input-dark mt-1" placeholder="e.g., Rebel Alliance" data-testid="contribution-affiliation" />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Abilities (comma-separated)</Label>
                  <Input value={form.abilities} onChange={(e) => setForm({ ...form, abilities: e.target.value })} className="input-dark mt-1" placeholder="e.g., Flight, Super Strength, Telepathy" data-testid="contribution-abilities" />
                </div>
              </div>
            )}

            {/* Images */}
            <div>
              <Label className="text-slate-300">Images {form.type === "fan_art" ? "(required)" : "(optional)"}</Label>
              <div className="mt-1">
                <MultiImageUploader
                  images={form.images}
                  onChange={(imgs) => setForm({ ...form, images: imgs })}
                  maxImages={5}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => navigate("/community")} data-testid="contribution-cancel">Cancel</Button>
              <Button type="submit" disabled={submitting} className="btn-primary gap-2" data-testid="contribution-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SubmitContributionPage;
